import { Grid } from '@mui/material';
import TextField from 'components/text-field';
import React, { FC } from 'react';
import { Controller, useForm } from 'react-hook-form';
import ContainerModal from './modal-container';
import { useMutation } from 'react-query';
import graphql from 'services/querys';
import { NewAddressType } from 'types/new-user';
import SelectField from 'components/select-field';
import { useTranslation } from 'react-i18next';

type DialogAddressProps = {
  open: boolean;
  onClose: () => void;
  partyId: string;
  onSuccess: (addresses: NewAddressType[]) => void;
  states: {
    code: string;
    name: string;
    parent: string;
  }[];
};

type formAddress = {
  street_address: string;
  country: string;
  kind: string;
  locality: string;
  postal_code: string;
  references: string;
  settlement: string;
  state: string;
};

const CreateAddressModal: FC<DialogAddressProps> = ({
  open,
  onClose,
  partyId,
  onSuccess,
  states,
}) => {
  const { t } = useTranslation();
  const { mutate: createAddress, ...responseCreateAddress } = useMutation(graphql.createAddress, {
    onSuccess: (data) => {
      onSuccess(data.createAddress?.addresses || []);
      onClose();
    },
  });
  const methods = useForm<formAddress>({
    defaultValues: {
      street_address: '',
      country: 'US',
      kind: '',
      locality: '',
      postal_code: '',
      references: '',
      settlement: '',
      state: '',
    },
  });

  const { control } = methods;
  const isSaving = responseCreateAddress.isLoading;

  return (
    <ContainerModal
      open={open}
      onClose={onClose}
      onCancel={onClose}
      onSave={() => {
        methods.handleSubmit((data) => {
          createAddress({
            partyId,
            kind: data.kind,
            data: {
              country: 'US',
              locality: data.locality,
              postal_code: data.postal_code,
              references: data.references,
              settlement: data.settlement,
              state: data.state,
              street_address: data.street_address,
            } as NewAddressType,
          });
        })();
      }}
      disableSavebutton={!methods.formState.isValid}
      loadingSaveButton={isSaving}
      modalTitle={t('contact:addAddressModalTitle')}
    >
      <form>
        <Grid container direction="column" spacing={2}>
          <Grid item>
            <Controller
              control={control}
              name="street_address"
              rules={{ required: true }}
              render={({ field: { ref, ...field } }) => (
                <TextField {...field} inputRef={ref} disabled={isSaving} label={t('contact:streetAddressLabel')} />
              )}
            />
          </Grid>
          <Grid item>
            <Controller
              control={control}
              name="postal_code"
              rules={{ required: true }}
              render={({ field: { ref, ...field } }) => <TextField {...field} inputRef={ref} disabled={isSaving} label={t('contact:zipCodeLabel')} />}
            />
          </Grid>
          <Grid item>
            <Controller
              control={control}
              name="settlement"
              rules={{ required: true }}
              render={({ field: { ref, ...field } }) => <TextField {...field} inputRef={ref} disabled={isSaving} label={t('contact:countyLabel')} />}
            />
          </Grid>
          <Grid item>
            <Controller
              control={control}
              name="locality"
              rules={{ required: true }}
              render={({ field: { ref, ...field } }) => <TextField {...field} inputRef={ref} disabled={isSaving} label={t('contact:cityLabel')} />}
            />
          </Grid>
          <Grid item>
            <Controller
              control={control}
              name="state"
              rules={{ required: true }}
              render={({ field: { ref, ...field } }) => (
                <SelectField
                  {...field}
                  inputRef={ref}
                  disabled={isSaving}
                  label={t('contact:stateLabel')}
                  data={states.filter(state => state.code !== 'ALL')}
                  dataKey="code"
                  dataValue="code"
                  dataText="name"
                  handleChange={field.onChange}
                />
              )}
            />
          </Grid>
          <Grid item>
            <Controller
              control={control}
              name="country"
              rules={{ required: true }}
              render={({ field: { ref, ...field } }) => (
                <TextField {...field} inputRef={ref} label={t('contact:countryLabel')} disabled />
              )}
            />
          </Grid>
          <Grid item>
            <Controller
              control={control}
              name="references"
              render={({ field: { ref, ...field } }) => <TextField {...field} inputRef={ref} disabled={isSaving} label={t('contact:referenceLabel')} />}
            />
          </Grid>
          <Grid item>
            <Controller
              control={control}
              name="kind"
              rules={{ required: true }}
              render={({ field: { ref, ...field } }) => <TextField {...field} inputRef={ref} disabled={isSaving} label={t('contact:kindLabel')} />}
            />
          </Grid>
        </Grid>
      </form>
    </ContainerModal>
  );
};

export default CreateAddressModal;
